import { useMemo } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Box, { Flex } from '@spotnana/pixel-react/dist/Box';
import Grid from '@spotnana/pixel-react/dist/Grid';
import Tooltip from '@spotnana/pixel-react/dist/Tooltip';
import { dateUtils, ILayoverInfo, useHasUserAccess, userRolesByFeature } from 'obt-common';

import { Typography } from '@spotnana/blocks/src/Typography';
import { ThirdPartySource } from 'obt-common/types/api/v1/obt/supplier/third_party_info';
import CopyableText from 'src/v2-components/shared/CopyableText';
import { AirlineImage } from 'src/v2-components/flights/shared/AirlineImage';
import { getItinerarySourceLabelAir } from 'obt-common/utils/Flights/getItinerarySourceLabelAir';
import { HiddenStops } from 'src/app/flights/shared/HiddenStops';
import DotSeperator from '../../../../../../v2-components/shared/DotSeparator';
import getBrandCabinBookingCodeText from '../../../../../../v2-components/flights/shared/utils/getBrandCabinBookingCodeText';

import UatpPassPlus from './UatpPassPlus';
import FlightSegmentContainer from './FlightSegmentContainer';
import { flight_marketing_label, strike_through, hidden_layover_item } from './styles';
import FlightSegmentDateTime from '../FlightSegmentDateTime';

const { getDateDiff } = dateUtils;

interface IFlightSegmentProps {
  legDepartureDateTime: string;
  departureDateTime: string;
  originalDepartureDateTime?: string;
  arrivalDateTime: string;
  originalArrivalDateTime?: string;
  arrivalAirport: string;
  arrivalAirportName: string;
  departureAirport: string;
  departureAirportName: string;
  marketingAirlineNumber?: string;
  marketingAirlineLabel?: string;
  originalMarketingAirlineNumber?: string;
  originalMarketingAirlineLabel?: string;
  operatingAirlineLabel: string;
  flightDuration: string;
  brandName: string;
  bookingCode: string;
  cabin: string;
  isUtaPassPlusRate: boolean;
  isMixedCabin: boolean;
  flightSourceStatus: string;
  hasExtraInfoAccess: boolean;
  airlineReference: string;
  itinerarySource?: ThirdPartySource;
  rateOptionAirline: string;
  hiddenLayovers: ILayoverInfo[];
}

const FlightSegmentDetails: React.FC<IFlightSegmentProps> = ({
  departureDateTime,
  originalDepartureDateTime,
  arrivalDateTime,
  originalArrivalDateTime,
  arrivalAirport,
  arrivalAirportName,
  departureAirport,
  departureAirportName,
  marketingAirlineNumber,
  originalMarketingAirlineNumber,
  marketingAirlineLabel,
  originalMarketingAirlineLabel,
  operatingAirlineLabel,
  legDepartureDateTime,
  flightDuration,
  brandName,
  bookingCode,
  cabin,
  isUtaPassPlusRate,
  isMixedCabin,
  flightSourceStatus,
  hasExtraInfoAccess,
  airlineReference,
  itinerarySource,
  rateOptionAirline,
  hiddenLayovers,
}: IFlightSegmentProps) => {
  const { t: tt } = useTranslation('WEB');

  const arrivalDateChange =
    legDepartureDateTime && arrivalDateTime ? getDateDiff(departureDateTime, arrivalDateTime) : 0;

  const flightMarketingAirline = marketingAirlineLabel ?? '';
  const isOvernight = arrivalDateChange > 0;

  const bookingCodeAndCabin = useMemo(
    () =>
      getBrandCabinBookingCodeText({
        brandName,
        cabin,
        bookingCode,
        isMixedCabin,
      }),
    [brandName, cabin, bookingCode, isMixedCabin],
  );

  const isAgent = useHasUserAccess(userRolesByFeature.organizationSelector);
  const source = itinerarySource ? getItinerarySourceLabelAir(itinerarySource, rateOptionAirline) : '';
  const showItinSource = isAgent;

  return (
    <Flex mt={4} flexDirection="column" data-testid="flight-segment-container">
      <Grid.Row data-testid="flight-segment-airline">
        <Grid.Col>
          <AirlineImage marketingAirline={flightMarketingAirline} data-testid="airline-logo" />
        </Grid.Col>
        <Grid.Col xs={10} sm={11}>
          <div css={flight_marketing_label}>
            {originalMarketingAirlineLabel && originalMarketingAirlineNumber && (
              <Typography variant="body1" css={strike_through} color="disabled">
                {`${originalMarketingAirlineLabel} ${originalMarketingAirlineNumber}`}
              </Typography>
            )}

            <Typography variant="body1" kind="semibold" data-testid="leg-marketing-airline-number">
              {`${marketingAirlineLabel} ${marketingAirlineNumber}`}
            </Typography>
          </div>

          <Flex
            flexWrap="wrap"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            data-testid="flight-segment-details"
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Typography variant="body2" color="secondary" data-testid="leg-operated-by-airline">
              {tt('Operated by {{operator}}', { operator: operatingAirlineLabel })}
            </Typography>
            <Box display={{ xs: 'none', sm: 'initial' }}>
              <DotSeperator size={4} gap={8} />
            </Box>
            <Typography variant="body2" color="secondary">
              {flightDuration}
            </Typography>

            {bookingCodeAndCabin && (
              <>
                <Box display={{ xs: 'none', sm: 'initial' }}>
                  <DotSeperator size={4} gap={8} />
                </Box>
                <Typography variant="body2" color="secondary">
                  {bookingCodeAndCabin}
                </Typography>
              </>
            )}

            {hasExtraInfoAccess && (
              <>
                <Box display={{ xs: 'none', sm: 'initial' }}>
                  <DotSeperator size={4} gap={8} />
                </Box>
                <Typography variant="body2" color="secondary" data-testid="flight-status-code-test-id">
                  {tt('Status code: {{statusCode}}', { statusCode: flightSourceStatus })}
                </Typography>
              </>
            )}

            {isOvernight && (
              <>
                <Box display={{ xs: 'none', sm: 'initial' }}>
                  <DotSeperator size={4} gap={8} />
                </Box>
                <Typography variant="body2" color="secondary">
                  {tt('Overnight')}
                </Typography>
              </>
            )}

            {showItinSource && source && (
              <>
                <Box display={{ xs: 'none', sm: 'initial' }}>
                  <DotSeperator size={4} gap={8} />
                </Box>
                <Typography variant="body2" color="secondary" data-testid="itin-source">
                  {`${tt('Source')}: ${source}`}
                </Typography>
              </>
            )}

            {!!airlineReference && (
              <>
                <Box display={{ xs: 'none', sm: 'initial' }}>
                  <DotSeperator size={4} gap={8} />
                </Box>
                <Typography variant="body2" color="secondary" data-testid="air-pnr">
                  {tt('Airline reference')}:
                </Typography>
                <CopyableText text={airlineReference} />
              </>
            )}
          </Flex>
        </Grid.Col>
      </Grid.Row>

      <Grid.Row data-testid="flight-segment-departure-arrival">
        <Grid.Col xs={12} sm={6}>
          <FlightSegmentContainer title={tt('Departure')}>
            <FlightSegmentDateTime
              originalDateTime={originalDepartureDateTime}
              dateTime={departureDateTime}
              daysChanged={null}
            />
            <Tooltip placement="top" arrow title={undefined}>
              <Typography variant="body2" color="secondary" data-testid="departure_airport">
                {`${departureAirportName} (${departureAirport})`}
              </Typography>
            </Tooltip>
            {hiddenLayovers.map((layover, layoverIndex) => (
              <div css={hidden_layover_item} key={layoverIndex}>
                <HiddenStops layover={layover} />
              </div>
            ))}
          </FlightSegmentContainer>
        </Grid.Col>

        <Grid.Col xs={12} sm={6}>
          <FlightSegmentContainer title={tt('Arrival')}>
            <FlightSegmentDateTime
              originalDateTime={originalArrivalDateTime}
              dateTime={arrivalDateTime}
              daysChanged={arrivalDateChange}
            />

            <Tooltip placement="top" arrow title={undefined}>
              <Typography variant="body2" color="secondary" data-testid="arrival-airport">
                {`${arrivalAirportName} (${arrivalAirport})`}
              </Typography>
            </Tooltip>
          </FlightSegmentContainer>
        </Grid.Col>
      </Grid.Row>
      {isUtaPassPlusRate && <UatpPassPlus />}
    </Flex>
  );
};

export default FlightSegmentDetails;
